import React from 'react';
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getData } from "../../../actions/apiCall";
import { Button, notification } from "antd";
import type { ColumnsType } from 'antd/es/table';
import { Table, Checkbox } from 'antd';
import { postData, putData } from '../../../actions/developer';


interface ItemRecord {
    status?: string;
    _id?: string;
    name: string;
    parentMenu?: string;
    createdAt?: string;
    updatedAt?: string;
    subparentMenu?: string;
    routeUrl?: string;
    componentName?: string;
    icon?: string;
    order: number;
    subMenu?: string;
    mainMenuRef?: any;
    subMenuRef?: any;
    parentMenuRef?: any;
    newTab?: any;
    menuId?: string;
    parentId?: string;
    list?: ItemRecord[];
    isNew?: boolean;
}

const EditAccess = ({ mainMenu, role, isPreview }) => {
    const dispatch = useDispatch();
    const [items, setItems] = useState<ItemRecord[]>([]);

    const sort = (a: ItemRecord, b: ItemRecord) => {
        return a.order - b.order;
    }

    useEffect(() => {
        if (!mainMenu) return;
        getTabsList();
    }, [mainMenu]);

    const getTabsList = async () => {
        const resData = await dispatch(getData(`/routing/tab/list/${mainMenu._id}`, { role, isPreview }));
        if (resData.code === 200) {
            setItems(resData.data.sort(sort));
        } else {
            notification.error({ message: 'Error', description: resData.message });
        }
    }

    return (
        <div>

            <PermissionTable rowData={items} role={role} isPreview={isPreview} />
        </div>
    );
}

export default EditAccess;


const permissionTypes = ["view", "edit", "create", "private"];

type PermissionType = {
    view: boolean;
    edit: boolean;
    create: boolean;
    private: boolean;
};

interface PermissionRow {
    id: string;
    name: string;
    parentId: string | null;
    level: number;
    isGroup: boolean;
    permissions: PermissionType;
    align?: string;
}

const buildTree = (items: any[]) => {
    const map = new Map();
    const roots: any[] = [];

    items.forEach((item) => {
        item.children = [];
        map.set(item._id, item);
    });

    items.forEach((item) => {
        if (item.parentId && map.has(item.parentId)) {
            map.get(item.parentId).children.push(item);
        } else {
            roots.push(item);
        }
    });

    return roots;
};

const flattenTree = (tree: any[], level = 0): PermissionRow[] => {
    let result: PermissionRow[] = [];

    for (const node of tree) {
        result.push({
            id: node._id,
            name: node.name,
            parentId: node.parentId || null,
            level,
            isGroup: true,
            permissions: {
                view: node.permissions?.view || false,
                edit: node.permissions?.edit || false,
                create: node.permissions?.create || false,
                private: node.permissions?.private || false,
            },
        });

        if (node.children && node.children.length > 0) {
            result = result.concat(flattenTree(node.children, level + 1));
        }
    }

    return result;
};

const PermissionTable: React.FC<{ rowData: ItemRecord[], role: string, isPreview: boolean }> = ({ rowData, role, isPreview }) => {
    const [data, setData] = useState<PermissionRow[]>([]);
    const dispatch = useDispatch();

    useEffect(() => {
        const tree = buildTree(rowData);
        const flattened = flattenTree(tree);
        setData(flattened);
    }, [rowData]);

    const updatePermissions = async () => {
        if (data.length === 0 || !role || isPreview) return;

        try {
            const resData = await dispatch(putData(`/access/bulk/update/${role}`, { data }))
            if (!resData) {
                notification.error({ message: 'Error', description: resData.message });
            } else {
                notification.success({ message: 'Success', description: 'Permissions updated successfully' });
            }
        } catch (error) {
            console.error("Error updating permissions:", error);
            notification.error({ message: 'Error', description: 'Failed to update permissions' });
        }
    }

    const getChildren = (parentId: string): PermissionRow[] => {
        const children: PermissionRow[] = [];
        const queue = [parentId];

        while (queue.length) {
            const currentId = queue.shift();
            for (const item of data) {
                if (item.parentId === currentId) {
                    children.push(item);
                    queue.push(item.id);
                }
            }
        }

        return children;
    };

    const getParents = (childId: string): string[] => {
        const parents: string[] = [];
        const map = new Map(data.map((d) => [d.id, d]));

        let current = map.get(childId);
        while (current && current.parentId) {
            parents.push(current.parentId);
            current = map.get(current.parentId);
        }

        return parents;
    };

    const togglePermission = (id: string, perm: keyof PermissionType, checked: boolean) => {
        const newData = [...data];
        const map = new Map(newData.map((d) => [d.id, d]));

        const current = map.get(id);
        if (!current) return;

        current.permissions[perm] = checked;

        if (checked && (perm === 'edit' || perm === 'create' || perm === 'private')) {
            current.permissions['view'] = true;
        }

        if (!checked && perm === 'view') {
            current.permissions['edit'] = false;
            current.permissions['create'] = false;
            current.permissions['private'] = false;
        }

        const children = getChildren(id);
        children.forEach((child) => {
            child.permissions[perm] = checked;

            if (checked && (perm === 'edit' || perm === 'create' || perm === 'private')) {
                child.permissions['view'] = true;
            }

            if (!checked && perm === 'view') {
                child.permissions['edit'] = false;
                child.permissions['create'] = false;
                child.permissions['private'] = false;
            }
        });

        const parents = getParents(id);
        for (const parentId of parents) {
            const parent = map.get(parentId);
            if (!parent) continue;

            const siblings = newData.filter((d) => d.parentId === parentId);
            const all = siblings.every((s) => s.permissions[perm]);
            const some = siblings.some((s) => s.permissions[perm]);

            parent.permissions[perm] = all;

            if ((perm === 'edit' || perm === 'create' || perm === 'private') && some) {
                parent.permissions['view'] = true;
            }
        }

        setData(newData);
    };


    const isIndeterminate = (record: PermissionRow, perm: keyof PermissionType): boolean => {
        const children = data.filter((d) => d.parentId === record.id);
        if (children.length === 0) return false;

        const all = children.every((c) => c.permissions[perm]);
        const some = children.some((c) => c.permissions[perm]);

        return some && !all;
    };

    const columns: ColumnsType<PermissionRow> = [
        {
            title: "Menu",
            dataIndex: "name",
            key: "name",
            render: (text, record) => (
                <span style={{ paddingLeft: `${record.level * 20}px`, fontWeight: record.level == 0 ? "bold" : "normal" }}>
                    {text}
                </span>
            ),
        },
        ...permissionTypes.map((perm) => ({
            title: perm.toUpperCase(),
            key: perm,
            align: "center",
            width: 80,
            render: (_, record: PermissionRow) => (
                <Checkbox
                    disabled={!role}
                    checked={record.permissions[perm as keyof PermissionType]}
                    indeterminate={isIndeterminate(record, perm as keyof PermissionType)}
                    onChange={(e) => togglePermission(record.id, perm as keyof PermissionType, e.target.checked)}
                />
            ),
        })) as ColumnsType<PermissionRow>,
    ];

    return (
        <>
            <div className='flex justify-start items-center mb-4'>
                <Button onClick={() => updatePermissions()} disabled={data.length === 0 || !role || isPreview}>Save Changes</Button>
            </div >
            <div style={{ padding: 20 }}>
                <Table
                    dataSource={data}
                    columns={columns}
                    rowKey="id"
                    rowClassName={(record) =>
                        record.level === 0 ? 'bg-gray-100' : ''
                    }
                    pagination={false}
                    bordered
                />
            </div>
        </>
    );
};

